import { useState, useCallback } from 'react';
import { copyAreaIndicator } from '../../../services/b2b-plans-apiService';
import { useLoginContext } from '../../../App';

/**
 * Hook to manage the copy of area indicators
 * @returns {Object} Object containing copy function, loading state, error state, and success state
 */
const useCopyAreaIndicator = () => {
    const { loginData } = useLoginContext();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);

    /**
     * Copy an area indicator from source to target area
     * @param {string} sourcePlanUUID - UUID of the source plan
     * @param {string} sourceAreaId - ID of the source area
     * @param {string} areaIndicatorId - ID of the area indicator to copy
     * @param {string} targetPlanUUID - UUID of the target plan
     * @param {string} targetAreaId - ID of the target area
     * @returns {Promise<Object>} Promise that resolves with the copy result
     */
    const copyAreaIndicatorWithTiming = useCallback(async (
        sourcePlanUUID,
        sourceAreaId,
        areaIndicatorId,
        targetPlanUUID,
        targetAreaId
    ) => {
        setLoading(true);
        setError(null);
        setIsSuccess(false);

        try {
            const result = await copyAreaIndicator(
                loginData.accessToken,
                sourcePlanUUID,
                sourceAreaId,
                areaIndicatorId,
                targetPlanUUID,
                targetAreaId
            );

            setIsSuccess(true);

            return {
                success: true,
                result
            };
        } catch (err) {
            console.error('Error copying area indicator:', err);
            setError(err);

            return {
                success: false,
                error: err
            };
        } finally {
            setLoading(false);
        }
    }, [loginData.accessToken]);

    /**
     * Reset the hook state
     */
    const resetState = useCallback(() => {
        setLoading(false);
        setError(null);
        setIsSuccess(false);
    }, []);

    return {
        copyAreaIndicator: copyAreaIndicatorWithTiming,
        loading,
        error,
        isSuccess,
        resetState
    };
};

export default useCopyAreaIndicator;
